import { useState, useEffect, useMemo } from 'react'
import { Menu, Sun, Moon, Monitor, ChevronLeft, ChevronRight } from 'lucide-react'
import { MainNav, type NavItem } from './MainNav'
import { UserMenu, type UserMenuProps } from './UserMenu'
import { useLabels } from '@/lib/terminology'

export interface AppShellProps {
  children: React.ReactNode
  user?: UserMenuProps['user']
  teamName?: string
  onLogout?: () => void
}

type Theme = 'light' | 'dark' | 'system'

export function AppShell({
  children,
  user,
  teamName = 'My Team',
  onLogout,
}: AppShellProps) {
  const labels = useLabels()

  const navigationItems: NavItem[] = useMemo(() => [
    { label: labels.pipeline, href: '/pipeline', icon: 'pipeline' },
    { label: labels.collections, href: '/collections', icon: 'series' },
    { label: labels.recordings, href: '/recordings', icon: 'recordings' },
    { label: labels.guests, href: '/guests', icon: 'guests' },
    { label: labels.schedule, href: '/schedule', icon: 'schedule' },
    { label: 'Integrations', href: '/integrations', icon: 'integrations' },
    { isSeparator: true, label: '', href: '' },
    { label: 'Settings', href: '/settings', icon: 'settings' },
    { label: 'Help', href: '/help', icon: 'help' },
  ], [labels])
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobileExpanded, setIsMobileExpanded] = useState(false)
  const [theme, setTheme] = useState<Theme>('system')

  // Initialize theme from localStorage (runs once on mount via useState initializer pattern)
  // Note: We use useEffect for this to avoid SSR hydration mismatches
  useEffect(() => {
    const savedTheme = localStorage.getItem('floaty-theme') as Theme | null
    if (savedTheme && savedTheme !== theme) {
      setTheme(savedTheme)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally only run on mount

  // Apply theme changes to document
  useEffect(() => {
    const root = document.documentElement

    if (theme === 'system') {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      root.classList.toggle('dark', systemDark)
    } else {
      root.classList.toggle('dark', theme === 'dark')
    }

    localStorage.setItem('floaty-theme', theme)
  }, [theme])

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      document.documentElement.classList.toggle('dark', e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  const cycleTheme = () => {
    setTheme(current => {
      if (current === 'light') return 'dark'
      if (current === 'dark') return 'system'
      return 'light'
    })
  }

  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor

  return (
    <div className="flex h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-40
          flex flex-col
          bg-white dark:bg-zinc-900
          border-r border-zinc-200 dark:border-zinc-800
          transition-all duration-200 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-60'}
          ${isMobileExpanded ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className={`flex items-center h-16 px-4 border-b border-zinc-200 dark:border-zinc-800 ${isCollapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm bg-primary-500">
            F
          </div>
          {!isCollapsed && (
            <span className="ml-3 font-semibold text-zinc-900 dark:text-zinc-100">
              Floaty
            </span>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <MainNav
            items={navigationItems}
            isCollapsed={isCollapsed}
          />
        </nav>

        {/* User Menu */}
        <div className="border-t border-zinc-200 dark:border-zinc-800">
          <UserMenu
            user={user}
            teamName={teamName}
            isCollapsed={isCollapsed}
            onLogout={onLogout}
            theme={theme}
            onThemeChange={cycleTheme}
          />
        </div>

        {/* Collapse Toggle Button - only on desktop */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 items-center justify-center rounded-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
          )}
        </button>
      </aside>

      {/* Mobile overlay */}
      {isMobileExpanded && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsMobileExpanded(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between h-16 px-4 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center">
            <button
              onClick={() => setIsMobileExpanded(true)}
              className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              <Menu className="w-6 h-6 text-zinc-600 dark:text-zinc-400" />
            </button>
            <div className="ml-3 w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm bg-primary-500">
              F
            </div>
            <span className="ml-2 font-semibold text-zinc-900 dark:text-zinc-100">
              Floaty
            </span>
          </div>
          <button
            onClick={cycleTheme}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
            title={theme === 'light' ? 'Light mode' : theme === 'dark' ? 'Dark mode' : 'System theme'}
          >
            <ThemeIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="h-full lg:h-screen overflow-auto">
          {children}
        </div>
      </main>
    </div>
  )
}
